import { Component, ChangeDetectionStrategy, computed, input, signal } from '@angular/core';

/** Un segment de l'anneau. */
export interface SegmentAnneau { cle: string; label: string; valeur: number; couleur: string; }

const RAYON = 60;
const CIRCONFERENCE = 2 * Math.PI * RAYON;
/** Trait de fond entre deux segments, en unités du tracé. */
const ESPACE = 2;

/**
 * Anneau (part d'un tout, 6 segments au plus) avec sa légende chiffrée : la légende porte
 * libellé, valeur et part — l'identité ne repose jamais sur la couleur seule. Le centre
 * affiche le total, ou le segment survolé.
 */
@Component({
  selector: 'app-anneau',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Toujours empilé : l'anneau vit dans une colonne étroite (1/3), une légende à côté
         n'y tient pas avec des libellés comme « Salaire / rémunération ». -->
    <div class="flex flex-col items-center gap-5">
      <div class="relative w-40 h-40 flex-shrink-0">
        <svg viewBox="0 0 160 160" class="w-full h-full -rotate-90">
          <circle cx="80" cy="80" [attr.r]="rayon" fill="none" stroke="#f5f5f4" stroke-width="18"></circle>
          @for (s of arcs(); track s.cle; let i = $index) {
            <circle cx="80" cy="80" [attr.r]="rayon" fill="none"
                    [attr.stroke]="s.couleur"
                    [attr.stroke-width]="survol() === i ? 22 : 18"
                    [attr.stroke-dasharray]="s.longueur + ' ' + circonference"
                    [attr.stroke-dashoffset]="-s.debut"
                    class="transition-[stroke-width] duration-150 cursor-default"
                    (mouseenter)="survol.set(i)" (mouseleave)="survol.set(null)"></circle>
          }
        </svg>
        <div class="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none px-6">
          @if (survol() !== null) {
            <span class="text-lg font-bold text-stone-800 tabular-nums leading-tight">{{ segments()[survol()!].valeur | number:format() }}</span>
            <span class="text-[11px] text-stone-500 leading-tight line-clamp-2">{{ segments()[survol()!].label }}</span>
          } @else {
            <span class="text-xl font-bold text-stone-800 tabular-nums leading-tight">{{ total() | number:format() }}</span>
            <span class="text-[11px] text-stone-500 leading-tight">{{ libelleTotal() }}</span>
          }
        </div>
      </div>

      <ul class="w-full min-w-0 space-y-1.5">
        @for (s of segments(); track s.cle; let i = $index) {
          <li class="flex items-center gap-2 text-sm rounded-lg px-2 py-1 -mx-2"
              [class.bg-stone-50]="survol() === i"
              (mouseenter)="survol.set(i)" (mouseleave)="survol.set(null)">
            <span class="w-2.5 h-2.5 rounded-sm flex-shrink-0" [style.background-color]="s.couleur"></span>
            <span class="flex-1 min-w-0 truncate text-stone-600">{{ s.label }}</span>
            <span class="font-medium text-stone-800 tabular-nums">{{ s.valeur | number:format() }}</span>
            <span class="w-10 text-right text-xs text-stone-400 tabular-nums">{{ part(s.valeur) }} %</span>
          </li>
        }
      </ul>
    </div>
  `,
})
export class AnneauComponent {
  segments = input.required<SegmentAnneau[]>();
  libelleTotal = input('Total');
  /** Format du pipe `number` : '1.0-0' pour des effectifs, '1.2-2' pour des montants. */
  format = input('1.0-0');

  readonly rayon = RAYON;
  readonly circonference = CIRCONFERENCE;
  survol = signal<number | null>(null);

  total = computed(() => this.segments().reduce((t, s) => t + s.valeur, 0));

  /** Arcs successifs ; 2 unités de fond entre segments dès qu'il y en a plus d'un. */
  arcs = computed(() => {
    const t = this.total();
    const visibles = this.segments().filter(s => s.valeur > 0).length;
    const espace = visibles > 1 ? ESPACE : 0;
    let debut = 0;
    return this.segments().map(s => {
      const plein = t > 0 ? (s.valeur / t) * CIRCONFERENCE : 0;
      const arc = { cle: s.cle, couleur: s.couleur, debut, longueur: Math.max(0, plein - espace) };
      debut += plein;
      return arc;
    });
  });

  part(v: number): string {
    const t = this.total();
    return t > 0 ? Math.round((v / t) * 100).toString() : '0';
  }
}
